import { api } from '../lib/api.js';
import type {
  HolidayInput,
  LeaveRequestInput,
  LeaveTypeInput,
} from '@attendance/shared';

export interface LeaveType {
  id: string;
  name: string;
  code: string;
  daysPerYear: string;
  isPaid: boolean;
  accrualMethod: string;
  carryForward: boolean;
  carryForwardCap: string | null;
  requiresDocument: boolean;
  minNoticeDays: number;
  maxConsecutive: number | null;
  applicableGender: string | null;
  probationAllowed: boolean;
  isActive: boolean;
  color: string;
  description: string | null;
}

export interface LeaveBalance {
  id: string;
  leaveTypeId: string;
  year: number;
  entitled: string;
  carriedForward: string;
  used: string;
  pending: string;
  remaining: string;
  leaveType: { id: string; name: string; code: string; color: string };
}

export interface LeaveRequest {
  id: string;
  employeeId: string;
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  isHalfDay: boolean;
  halfDaySession: 'MORNING' | 'AFTERNOON' | null;
  totalDays: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  rejectionReason: string | null;
  createdAt: string;
  leaveType: { id: string; name: string; code: string; color: string };
  /** Leave filed until return whose return charge left that year's balance under zero (charged anyway, flagged). */
  belowZeroAfterReturn?: boolean;
  employee?: {
    id: string;
    employeeCode: string;
    firstName: string;
    lastName: string;
    displayName: string | null;
  };
}

export interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
  isRecurring: boolean;
  description: string | null;
}

export const leaveTypesApi = {
  list: () => api.get<{ data: LeaveType[] }>('/leave-types').then((r) => r.data),
  create: (input: LeaveTypeInput) =>
    api.post<{ data: LeaveType }>('/leave-types', input).then((r) => r.data),
  update: (id: string, input: LeaveTypeInput) =>
    api.patch<{ data: LeaveType }>(`/leave-types/${id}`, input).then((r) => r.data),
  remove: (id: string) => api.delete<{ data: { ok: true } }>(`/leave-types/${id}`),
};

/**
 * A leave "until return" still running (`GET /leave-balances`' `open`). It has
 * charged nothing yet, so the balances beside it read as if the worker were at
 * work; `daysSoFar` is what a return today would charge, by the return's own
 * counter.
 */
export interface OpenLeaveSoFar {
  id: string;
  /** The first day away, `YYYY-MM-DD`. */
  startDate: string;
  leaveType: { id: string; name: string; code: string; color: string };
  daysSoFar: number;
}

export const leaveBalancesApi = {
  list: (params?: { employeeId?: string; year?: number }) =>
    api
      .get<{ data: LeaveBalance[] }>('/leave-balances', { params })
      .then((r) => r.data),
  /** The balances AND the open leave, under the one reader gate the route applies to both. */
  withOpen: (params: { employeeId: string; year: number }) =>
    api.get<{ data: LeaveBalance[]; open: OpenLeaveSoFar | null }>('/leave-balances', { params }),
};

/**
 * The approver's queue — every PENDING request in scope — under the one key the
 * /leave/manage page and the rail's count badge both read. Same reasoning as
 * `correctionsQueryKey`: the badge counts the queue by BEING the queue's own
 * query, so the two cannot disagree and approving on the page moves the badge
 * with no second invalidation to remember.
 */
export const PENDING_LEAVE_REQUESTS_QUERY_KEY = [
  'leave-requests',
  'all',
  'PENDING',
] as const;

/** The rail's count of that queue, as a COUNT (A-07) — same predicate and scope server-side (`leaveRequestWhere`). */
export const PENDING_LEAVE_COUNT_QUERY_KEY = [...PENDING_LEAVE_REQUESTS_QUERY_KEY, 'count'] as const;

export const leaveRequestsApi = {
  async pendingCount(): Promise<number> {
    const body = await api.get<{ data: { count: number } }>('/leave-requests/count', {
      params: { filter: 'all', status: 'PENDING' },
    });
    return body.data.count;
  },
  list: (params?: { filter?: 'mine' | 'all'; status?: string; q?: string; leaveTypeId?: string }) =>
    api
      .get<{ data: LeaveRequest[] }>('/leave-requests', { params })
      .then((r) => r.data),
  submit: (input: LeaveRequestInput) =>
    api.post<{ data: LeaveRequest }>('/leave-requests', input).then((r) => r.data),
  approve: (id: string) =>
    api.patch<{ data: LeaveRequest }>(`/leave-requests/${id}/approve`).then((r) => r.data),
  reject: (id: string, reason?: string) =>
    api
      .patch<{ data: LeaveRequest }>(`/leave-requests/${id}/reject`, {
        rejectionReason: reason,
      })
      .then((r) => r.data),
  cancel: (id: string) => api.delete<{ data: LeaveRequest }>(`/leave-requests/${id}`),
  /**
   * Server-computed chargeable days for a prospective request — mirrors the
   * exact weekend/holiday rule submit() applies, so the form preview never
   * drifts from what gets booked. `daysByYear` is the same per-calendar-year
   * split `submit` stores, so a range across 31 December is read against each
   * year's own balance row (M-41).
   */
  previewDays: (params: {
    startDate: string;
    endDate: string;
    isHalfDay: boolean;
  }) =>
    api
      .get<{ data: { days: number; daysByYear?: Record<string, number> } }>('/leave-requests/preview', {
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          isHalfDay: params.isHalfDay,
        },
      })
      .then((r) => r.data),
};

export const holidaysApi = {
  list: (year?: number) =>
    api.get<{ data: Holiday[] }>('/holidays', { params: { year } }).then((r) => r.data),
  create: (input: HolidayInput) =>
    api.post<{ data: Holiday }>('/holidays', input).then((r) => r.data),
  update: (id: string, input: HolidayInput) =>
    api.patch<{ data: Holiday }>(`/holidays/${id}`, input).then((r) => r.data),
  remove: (id: string) => api.delete<{ data: { ok: true } }>(`/holidays/${id}`),
};
